import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { createTournament } from '../../store/slices/tournamentSlice';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
    Calendar,
    MapPin,
    DollarSign,
    Users,
    Image as ImageIcon,
    ChevronLeft,
    XCircle,
    Plus,
    Send,
    Loader2,
    Map,
    Trophy,
    Info,
    Check,
    Copy
} from 'lucide-react';

const InputGroup = ({ label, icon, children }) => (
    <div className="space-y-2">
        <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1 flex items-center gap-2">
            {icon}
            {label}
        </label>
        {children}
    </div>
);

const CreateTournament = () => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [sport, setSport] = useState('Fútbol');
    const [customSport, setCustomSport] = useState('');
    const [startDate, setStartDate] = useState('');
    // ... rest of states (endDate, location, maxTeams, registrationFee, prize, isPublic)
    const [endDate, setEndDate] = useState('');
    const [location, setLocation] = useState('');
    const [maxTeams, setMaxTeams] = useState('');
    const [registrationFee, setRegistrationFee] = useState('');
    const [prize, setPrize] = useState('');
    const [isPublic, setIsPublic] = useState(true);

    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [createdTournament, setCreatedTournament] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const [salon, setSalon] = useState(null);
    const [loadingSalon, setLoadingSalon] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uniqueSports, setUniqueSports] = useState([]);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { slug } = useParams();

    React.useEffect(() => {
        fetchSalon();
        fetchUniqueSports();
    }, [slug]);

    const fetchSalon = async () => {
        try {
            const { data } = await axios.get(`/api/clients/${slug}`);
            setSalon(data);
        } catch (err) {
            console.error('Error fetching salon:', err);
        } finally {
            setLoadingSalon(false);
        }
    };

    const fetchUniqueSports = async () => {
        try {
            const { data } = await axios.get(`/${slug}/api/tournaments/sports`);
            // Filtrar para no repetir los default y quitar nulos/vacíos
            const defaults = ['Fútbol', 'Pádel', 'Básquet', 'Vóley', 'Tenis', 'Rugby', 'E-Sports'];
            const customOnes = data.filter(s => s && !defaults.includes(s));
            setUniqueSports(customOnes);
        } catch (err) {
            console.error('Error fetching sports:', err);
        }
    };

    const isMpConnected = !!salon?.hasMpConnected;
    const isMpRequired = salon?.billingModel !== 'subscription' && !isMpConnected;

    const onPickCover = (e) => {
        const f = e.target.files?.[0];
        setCoverFile(f || null);
        setCoverPreview(f ? URL.createObjectURL(f) : '');
    };

    const removeCover = () => {
        setCoverFile(null);
        setCoverPreview('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSuccessMessage('');

        const finalSport = sport === 'Otro' ? customSport : sport;

        const formData = new FormData();
        formData.append('name', name);
        formData.append('description', description);
        formData.append('sport', finalSport);
        formData.append('startDate', startDate);
        formData.append('endDate', endDate);
        formData.append('location', location);
        formData.append('maxTeams', maxTeams);
        formData.append('registrationFee', registrationFee);
        formData.append('prize', prize);
        formData.append('isPublic', isPublic);

        if (coverFile) {
            formData.append('cover', coverFile);
        }

        try {
            const newTournament = await dispatch(createTournament({ slug, data: formData })).unwrap();

            setSuccessMessage('Torneo creado con éxito.');
            setCreatedTournament(newTournament);
            setShowShareModal(true);
        } catch (error) {
            console.error('Error al crear torneo:', error);
            alert(error.error || error.message || 'Error al crear el torneo.');
        } finally {
            setSaving(false);
        }
    };

    // --- Helpers para compartir ---
    const shareUrl = createdTournament
        ? `${process.env.REACT_APP_API_URL || window.location.origin}/share/${slug}/tournaments/${createdTournament._id}`
        : '';

    const handleCopyLink = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy', err);
        }
    };

    const encodedShareUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(`¡Anotá a tu equipo en ${createdTournament?.name}! \nInscripciones acá:`);

    return (
        <div className="min-h-screen bg-[#080808] text-white selection:bg-blue-500/30 pb-20 overflow-x-hidden">
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px] animate-glow" />
            </div>

            <div className="max-w-5xl mx-auto px-6 pt-12 relative z-10">
                <button
                    onClick={() => navigate(`/${slug}/admin/tournaments`)}
                    className="flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-8 group"
                >
                    <ChevronLeft size={20} className="transition-transform group-hover:-translate-x-1" />
                    <span className="font-black text-[10px] uppercase tracking-[2px]">Volver a Torneos</span>
                </button>

                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
                    <div>
                        <h1 className="text-4xl sm:text-6xl font-black tracking-tight mb-2 uppercase">Crear <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">Torneo</span></h1>
                        <p className="text-white/40 font-medium tracking-tight">Prepará la competencia y abrí las inscripciones para los equipos.</p>
                    </div>
                    {successMessage && (
                        <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-6 py-3 rounded-2xl font-bold animate-in fade-in zoom-in duration-300">
                            {successMessage}
                        </div>
                    )}
                </div>

                {!loadingSalon && isMpRequired && (
                    <div className="mb-12 bg-blue-500/10 border border-blue-500/20 p-8 rounded-[40px] flex flex-col md:flex-row items-center gap-6 animate-fade-in-up">
                        <div className="w-16 h-16 bg-blue-500/20 rounded-3xl flex items-center justify-center text-blue-400 shrink-0">
                            <XCircle size={32} />
                        </div>
                        <div className="flex-1 text-center md:text-left">
                            <h3 className="text-xl font-black uppercase text-blue-400 mb-1">Mercado Pago no conectado</h3>
                            <p className="text-white/60 font-medium leading-relaxed">
                                Para poder cobrar inscripciones y premios, necesitás vincular tu cuenta de Mercado Pago.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate(`/${slug}/admin/settings?focus=mp`)}
                            className="px-8 py-4 bg-blue-500 text-white font-black rounded-2xl hover:scale-105 transition-all shadow-lg shadow-blue-500/20"
                        >
                            CONECTAR AHORA
                        </button>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Main Info */}
                    <div className="lg:col-span-8 space-y-8">
                        <div className="bg-glass rounded-[40px] p-8 md:p-10 shadow-3xl space-y-8">
                            <InputGroup label="Nombre del Torneo" icon={<Trophy size={12} className="text-blue-500" />}>
                                <input
                                    type="text"
                                    placeholder="Ej: Copa Relámpago"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-lg font-black placeholder:text-white/10 focus:border-blue-500/50 transition-all focus:ring-4 focus:ring-blue-500/5 outline-none"
                                    required
                                />
                            </InputGroup>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                                <InputGroup label="Deporte" icon={<Info size={12} className="text-blue-500" />}>
                                    <div className="space-y-4">
                                        <select
                                            value={sport}
                                            onChange={(e) => setSport(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-blue-500/50 transition-all appearance-none"
                                            required
                                        >
                                            {[
                                                'Fútbol', 'Pádel', 'Básquet', 'Vóley', 'Tenis', 'Rugby', 'E-Sports',
                                                ...uniqueSports,
                                                'Otro'
                                            ].map(p => (
                                                <option key={p} value={p} className="bg-[#080808]">{p}</option>
                                            ))}
                                        </select>
                                        
                                        {sport === 'Otro' && (
                                            <input
                                                type="text"
                                                placeholder="¿Qué deporte es?"
                                                value={customSport}
                                                onChange={(e) => setCustomSport(e.target.value)}
                                                className="w-full bg-white/5 border border-blue-500/50 rounded-2xl p-5 text-white font-black outline-none transition-all placeholder:text-white/10 animate-fade-in-down"
                                                required
                                            />
                                        )}
                                    </div>
                                </InputGroup>

                                <InputGroup label="Sede del Torneo" icon={<MapPin size={12} className="text-blue-500" />}>
                                    <input
                                        type="text"
                                        placeholder="Ej: Complejo La Red"
                                        value={location}
                                        onChange={(e) => setLocation(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-blue-500/50 transition-all"
                                        required
                                    />
                                </InputGroup>
                            </div>

                            <InputGroup label="Descripción y Reglamento" icon={<Plus size={12} className="text-blue-500" />}>
                                <textarea
                                    placeholder="Contanos las reglas, premios y detalles formato..."
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-medium placeholder:text-white/10 focus:border-blue-500/50 transition-all focus:ring-4 focus:ring-blue-500/5 outline-none h-32 resize-none"
                                />
                            </InputGroup>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <InputGroup label="Fecha de Inicio" icon={<Calendar size={12} className="text-blue-500" />}>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black focus:border-blue-500/50 transition-all outline-none"
                                    />
                                </InputGroup>
                                <InputGroup label="Fecha de Finalización" icon={<Calendar size={12} className="text-blue-500" />}>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black focus:border-blue-500/50 transition-all outline-none"
                                    />
                                </InputGroup>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar / Config */}
                    <div className="lg:col-span-4 space-y-8">
                        {/* Portada */}
                        <div className="bg-glass rounded-[40px] p-8 shadow-3xl">
                            <InputGroup label="Banner del Torneo" icon={<ImageIcon size={12} className="text-blue-500" />}>
                                <div className="relative group/cover aspect-video bg-white/5 border-2 border-dashed border-white/10 rounded-3xl overflow-hidden mt-2">
                                    {coverPreview ? (
                                        <>
                                            <img src={coverPreview} alt="Preview" className="w-full h-full object-cover transition-transform duration-700 group-hover/cover:scale-110" />
                                            <button
                                                type="button"
                                                onClick={removeCover}
                                                className="absolute top-3 right-3 p-2 bg-black/60 backdrop-blur-md rounded-full text-red-400 hover:scale-110 transition-all border border-white/10"
                                            >
                                                <XCircle size={20} />
                                            </button>
                                        </>
                                    ) : (
                                        <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-white/[0.02] transition-colors">
                                            <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-white/20 mb-3 font-black">+</div>
                                            <span className="text-[10px] font-black uppercase tracking-[2px] text-white/20">Subir Banner</span>
                                            <input type="file" className="hidden" accept="image/*" onChange={onPickCover} />
                                        </label>
                                    )}
                                </div>
                            </InputGroup>
                        </div>

                        {/* Config & Inscriptions */}
                        <div className="bg-glass rounded-[40px] p-8 shadow-3xl space-y-8">

                            <InputGroup label="Cupo Máx. (Equipos)" icon={<Users size={12} className="text-blue-500" />}>
                                <input
                                    type="number"
                                    placeholder="Ej: 16"
                                    value={maxTeams}
                                    onChange={(e) => setMaxTeams(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-blue-500/50 transition-all"
                                    required
                                />
                            </InputGroup>

                            <InputGroup label="Precio Inscripción ($)" icon={<DollarSign size={12} className="text-blue-500" />}>
                                <input
                                    type="number"
                                    placeholder="Costo por equipo"
                                    value={registrationFee}
                                    onChange={(e) => setRegistrationFee(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-blue-500/50 transition-all"
                                    required
                                />
                            </InputGroup>

                            <InputGroup label="Premio" icon={<Trophy size={12} className="text-blue-500" />}>
                                <input
                                    type="text"
                                    placeholder="Ej: $100.000 + Trofeo"
                                    value={prize}
                                    onChange={(e) => setPrize(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-blue-500/50 transition-all"
                                />
                            </InputGroup>

                            {/* Toggle Public / Private */}
                            <div className="flex items-center justify-between p-5 bg-white/5 border border-white/10 rounded-2xl">
                                <div>
                                    <h4 className="text-sm font-black text-white">Torneo Público</h4>
                                    <p className="text-[10px] text-white/40 mt-1">Si está activo, aparecerá en tu perfil de organizador y en buscar.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer ml-4">
                                    <input
                                        type="checkbox"
                                        checked={isPublic}
                                        onChange={(e) => setIsPublic(e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-14 h-7 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                                </label>
                            </div>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={saving || (!loadingSalon && isMpRequired)}
                            className="group w-full bg-gradient-to-r from-blue-600 to-blue-400 py-6 rounded-[32px] font-black text-white text-lg shadow-xl shadow-blue-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-3 shine-effect"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="animate-spin" size={24} />
                                    GUARDANDO...
                                </>
                            ) : isMpRequired && !loadingSalon ? (
                                <>
                                    <XCircle size={24} />
                                    MP REQUERIDO
                                </>
                            ) : (
                                <>
                                    <Send size={24} />
                                    PUBLICAR TORNEO
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* --- MODAL DE ÉXITO --- */}
            {showShareModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-6">
                    <div className="bg-glass border border-white/10 rounded-[32px] max-w-md w-full p-8 relative animate-in zoom-in-90 fade-in duration-300">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                                <Check size={40} className="text-green-500" />
                            </div>

                            <h2 className="text-2xl font-black text-white mb-2 uppercase tracking-wide">¡Torneo Creado!</h2>
                            <p className="text-white/60 mb-8 font-medium">Abierto a inscripciones. ¡Compartilo para que se anoten los equipos!</p>

                            <a
                                href={`https://wa.me/?text=${encodedText}%20${encodedShareUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl text-white font-bold mb-4 bg-[#25D366] hover:bg-[#1dbf57] transition-transform hover:scale-[1.02] shadow-lg`}
                            >
                                Compartir en WhatsApp
                            </a>

                            {/* Copiar Link */}
                            <button
                                onClick={handleCopyLink}
                                className="w-full bg-white/5 border border-white/10 hover:bg-white/10 py-4 rounded-xl text-white/80 font-bold mb-8 flex items-center justify-center gap-2 transition-all"
                            >
                                {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
                                {copied ? '¡Enlace Copiado!' : 'Copiar enlace al torneo'}
                            </button>

                            <div className="flex flex-col w-full gap-3">
                                <button
                                    onClick={() => navigate(`/${slug}/admin/tournaments`)}
                                    className="bg-blue-600 py-3.5 rounded-xl font-bold text-white text-sm uppercase tracking-wider hover:opacity-90"
                                >
                                    Volver al Panel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default CreateTournament;
